<template>
	<div id="empresa-listar" :class="{ 'h-full': !hasEmpresas }">
		<section class="empresa-sub-header">

			<div class="empresa-sub-header-container flex">

				<div class="empresa-buscar-container rounded-lg mr-8">
					<div class="h-full z-50">
						<feather-icon
							icon="SearchIcon"
							class="empresa-buscar-icon"
						></feather-icon>
						<input
							type="text"
							placeholder="Buscar"
							class="empresa-buscar-input empresa-buscar-icon__search"
							v-model="buscaEmpresa"
							@input="pesquisarEmpresa"
						/>
						<feather-icon
							v-if="buscaEmpresa"
							icon="XIcon"
							class="empresa-buscar-icon empresa-buscar-icon__X"
							@click="limparBusca"
						></feather-icon>
					</div>
				</div>

				<div class="empresa-filtros-container flex ml-2" v-if="$permissions.can('autenticadosuperuser')">
					<label class="filtros-label flex items-center mr-2">Filtros:</label>
					<vs-radio class="mr-4" v-model="filtros.bloqueada" vs-value="true">Bloqueadas</vs-radio>
					<vs-radio class="mr-4" v-model="filtros.bloqueada" vs-value="false">Desbloqueadas</vs-radio>
					<vs-radio v-model="filtros.bloqueada" vs-value="todas">Todas</vs-radio>
				</div>
			</div>

			<div class="empresa-actions ml-8">
				<vs-button
					v-if="$permissions.can('autenticadosuperuser') || $permissions.can('empresaadicionar')"
					class="empresa-actions-button--adicionar"
					@click="popupAdicionarEmpresa = true"
					>Nova empresa</vs-button
				>
			</div>
		</section>

		<transition-group
			v-if="hasEmpresas"
			appear
			name="empresas"
			tag="ul"
			class="card-empresa-list vx-row"
		>
			<li
				class="card-empresa-list__item vx-col mb-base"
				v-for="empresa in empresas"
				:key="empresa.pk"
			>
				<vx-card :id="`empresa-${empresa.pk}`">
					<h5>{{ empresa.nome }}</h5>

					<div>
						<div class="flex justify-between flex-wrap">
							<vs-button
								:disabled="empresa.bloqueada"
								class="mt-4 w-3/5"
								:color="empresa.bloqueada ? 'white' : 'primary'"
								@click="() => entrarEmpresa(empresa)"
								>Entrar</vs-button
							>
							<vs-button
								v-if="$permissions.can('autenticadosuperuser') || $permissions.can('empresamodificar')"
								:disabled="empresa.bloqueada"
								class="mt-4 ml-3 flex-1"
								type="border"
								:color="empresa.bloqueada ? 'white' : '#b9b9b9'"
								@click="() => editarEmpresa(empresa)"
								>Editar</vs-button
							>
						</div>

						<div>
							<vs-divider />
							<div class="flex justify-between">
								<span class="flex items-center">
									<feather-icon
										icon="TargetIcon"
										:svgClasses="{
											'h-5 w-5 stroke-current text-warning': true,
											'text-white': empresa.bloqueada
										}"
									/>
									<span
										class="ml-2"
										:class="{
											'text-white': empresa.bloqueada
										}"
										>
										{{ empresa.quantidadePrazo }}
										{{
											empresa.quantidadePrazo != 1
												? "prazos"
												: "prazo"
										}}
										</span
									>
								</span>

								<span class="flex items-center">
									<feather-icon
										icon="UsersIcon"
										:svgClasses="{
											'h-5 w-5 stroke-current text-primary': true,
											'text-white': empresa.bloqueada
										}"
									/>
									<span
										class="ml-2"
										:class="{
											'text-white': empresa.bloqueada
										}"
									>
										{{ empresa.quantidadeColaborador }}
										{{
											empresa.quantidadeColaborador != 1
												? "usuários"
												: "usuário"
										}}</span
									>
								</span>
							</div>
						</div>
					</div>
					<div v-show="empresa.bloqueada" class="empresa-block">
						<feather-icon
							icon="LockIcon"
							svgClasses="empresa-block-icon"
						></feather-icon>
						<h5 class="empresa-block-title">
							Empresa bloqueada
						</h5>
						<vs-button
							v-if="$permissions.can('autenticadosuperuser') || $permissions.can('empresamodificar')"
							color="grey"
							class="mt-4 w-4/5"
							@click="() => showPopupReativarEmpresa(empresa)"
							>Desbloquear</vs-button
						>
					</div>
				</vx-card>
			</li>
		</transition-group>

		<div
			v-if="!hasEmpresas && Array.isArray(empresas)"
			class="sem-empresa-container h-full flex justify-center items-center"
		>
			<h3 class="sem-empresa-message">
				Nenhum empresa encontrada.
			</h3>
		</div>

		<div
			v-if="empresas"
			class="empresa-pagination-container"
		>
			<ld-pagination
				class="empresa-pagination"
				:currentPage.sync="pagination.currentPage"
				:offset.sync="pagination.offset"
				:pageSize="pagination.pageSize"
				:dataLength="pagination.dataLength"
			/>
		</div>

		<popup-empresa-adicionar
			:active.sync="popupAdicionarEmpresa"
			:closed="limparBusca"
		/>

		<popup-empresa-editar
			:active.sync="popupEditarEmpresa"
			:empresa="empresaEditar"
			:closed="() => {
				getEmpresasPorFiltro();
				empresaEditar = null;
			}"
		/>

		<popup-reativar-empresa
			v-if="popupReativarEmpresa"
			:show.sync="popupReativarEmpresa"
			:reativar="reativarEmpresa"
		/>
	</div>
</template>

<script>
import Empresa from "../../../models/Empresa";
import { Debounce } from "../../../helpers/EventHelper";
import PopupReativarEmpresa from "./components/PopupReativarEmpresa";
import LdPagination from "../../../components/ld-pagination/LdPagination";
import PopupEmpresaAdicionar from "../empresa_adicionar/EmpresaAdicionar";
import PopupEmpresaEditar from "../empresa_editar/EmpresaEditar";

import { mapActions } from "vuex";
import Colaborador from '../../../models/Colaborador';

export default {
	name: "Empresas",
	data() {
		return {
			empresas: null,
			buscaEmpresa: null,
			popupReativarEmpresa: false,
			popupAdicionarEmpresa: false,
			popupEditarEmpresa: false,
			empresaPkParaRaativar: null,
			pagination: this.initialPagination(),
			filtros: {
				search: "",
				bloqueada: "todas"
			},
			pesquisarEmpresaDebounce: new Debounce(),
			empresaEditar: null,
			loadingEmpresa: null,
			loadingBuscarEmpresa: null
		};
	},
	components: {
		PopupReativarEmpresa,
		PopupEmpresaAdicionar,
		PopupEmpresaEditar,
		LdPagination
	},
	computed: {
		hasEmpresas() {
			return this.empresas && !!this.empresas.length;
		}
	},
	methods: {
		async getEmpresas() {
			this.loadingEmpresa = this.$vsloading({
				color: "#000000",
				backgroundColor: "#ffffffba",
				opacity: 1,
				width: 44,
				height: 44
			});

			try {
				const params = {
					offset: 0,
					limit: this.pagination.pageSize
				};
				const { empresas, totalEmpresas } = await Empresa.getEmpresas(
					params
				);

				this.empresas = empresas;
				this.pagination.dataLength = totalEmpresas;
				setTimeout(this.loadingEmpresa.hide, 500);
			} catch (error) {
				console.error(error);
				setTimeout(this.loadingEmpresa.hide, 500);
			}
		},
		pesquisarEmpresa(inputEvent) {
			this.pesquisarEmpresaDebounce.debounce(500, () =>
				this.getEmpresasPorFiltro(inputEvent)
			);
		},
		async getEmpresasPorFiltro(inputEvent) {
			this.loadingBuscarEmpresa = this.$vsloading({
				color: "#000000",
				backgroundColor: "#ffffffba",
				opacity: 1,
				width: 44,
				height: 44
			});

			try {
				if (inputEvent && inputEvent.target) {
					this.filtros.search = inputEvent.target.value;
					this.pagination = this.initialPagination();
				}

				const params = {
					offset: this.pagination.offset,
					limit: this.pagination.pageSize,
					search: this.filtros.search,
				};

				if (this.filtros.bloqueada === "true" || this.filtros.bloqueada === "false") {
					params.bloqueada = this.filtros.bloqueada;
				}

				const {
					empresas,
					totalEmpresas
				} = await Empresa.getEmpresas(params);

				this.empresas = empresas;
				this.pagination.dataLength = totalEmpresas;
				setTimeout(this.loadingBuscarEmpresa.hide, 500);
			} catch (error) {
				console.error(error);
				setTimeout(this.loadingBuscarEmpresa.hide, 500);
			}
		},
		editarEmpresa(empresa) {
			this.empresaEditar = empresa;
			this.popupEditarEmpresa = true;
		},
		async reativarEmpresa() {
			this.popupReativarEmpresa = false;
			const loadingReativarEmpresa = this.$vsloading({
				container: `#empresa-${this.empresaReativar.pk}`,
				color: "#000000",
				backgroundColor: "#ffffffba",
				opacity: 1,
				width: 44,
				height: 44
			});

			try {
				await Empresa.editar({
					...this.empresaReativar,
					bloqueada: false
				});

				this.empresaReativar.bloqueada = false;
			} catch (error) {
				console.error(error);
			} finally {
				loadingReativarEmpresa.hide();
			}
		},
		showPopupReativarEmpresa(empresa) {
			this.empresaReativar = empresa;
			this.popupReativarEmpresa = true;
		},
		initialPagination() {
			return {
				currentPage: 1,
				offset: 0,
				pageSize: 12,
				dataLength: 0
			};
		},
		limparBusca() {
			this.buscaEmpresa = null;
			this.filtros.search = "";

			if (this.pagination.currentPage == 1) {
				this.getEmpresasPorFiltro();
			}

			this.pagination = this.initialPagination();
		},
		entrarEmpresa(empresa) {
			Empresa.entrarEmpresa(empresa);
			this.$navigate({
				name: "empresa-prazo-calendario",
				params: {
					empresaPk: empresa.pk
				}
			});
		},
		...mapActions(["setBreadcrumb"])
	},
	watch: {
		"pagination.currentPage": function() {
			this.getEmpresasPorFiltro(null);
		},
		"filtros.bloqueada": function() {
			this.getEmpresasPorFiltro(null);
		}
	},
	created() {
		this.setBreadcrumb({
			title: "Gestão de empresas",
			list: [{ name: "Gestão de empresas", active: true }]
		});

		Colaborador.removerColaborador();
		Empresa.setPermissoes();
		this.getEmpresas();
	},
	beforeDestroy() {
		if (this.loadingEmpresa) {
			this.loadingEmpresa.hide();
		}
		if (this.loadingBuscarEmpresa) {
			this.loadingBuscarEmpresa.hide();
		}
	}
};
</script>

<style lang="scss" scoped src="./empresas.scss"></style>
<style lang="scss" src="./empresa_global.scss"></style>
